/**
 * EntityOwnerTabSettings
 *
 * A dialog that allows entity owners to control which tabs are hidden
 * from non-owners. Hidden tabs can ONLY be seen by other owners of
 * that component.
 */
import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Chip,
  makeStyles,
  createStyles,
  Theme,
  CircularProgress,
  Divider,
} from '@material-ui/core';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { Alert } from '@material-ui/lab';
import { useEntityTabConfig } from './hooks/useEntityTabConfig';
import { Entity } from '@backstage/catalog-model';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogContent: {
      minWidth: 400,
      maxWidth: 520,
      [theme.breakpoints.down('sm')]: {
        minWidth: 0,
        maxWidth: 'none',
        width: 'auto',
      },
    },
    sectionTitle: {
      fontWeight: 600,
      marginBottom: theme.spacing(1),
    },
    description: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(2),
    },
    tabList: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
    },
    hiddenChip: {
      marginLeft: theme.spacing(1),
      height: 20,
      fontSize: '0.65rem',
    },
    summaryBox: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(1.5),
      backgroundColor: theme.palette.action.hover,
      borderRadius: theme.shape.borderRadius,
    },
  }),
);

interface EntityOwnerTabSettingsProps {
  open: boolean;
  onClose: () => void;
  entity: Entity;
  /** All available tab titles for this entity page */
  availableTabs: string[];
  /** Tab titles that should never be hidden (e.g. Overview, Docs) */
  protectedTabs?: string[];
}

export const EntityOwnerTabSettings = ({
  open,
  onClose,
  entity,
  availableTabs,
  protectedTabs = ['Overview', 'Docs'],
}: EntityOwnerTabSettingsProps) => {
  const classes = useStyles();
  const {
    ownerHiddenTabs,
    ownerVisibleTabs,
    setOwnerHiddenTabs,
    setOwnerVisibleTabs,
    securityFindingPrivateLock,
    securityFindingPrivateLockReason,
    loading,
  } = useEntityTabConfig(entity);
  const repoVisibility =
    entity.metadata.annotations?.['github.com/repo-visibility']?.toLowerCase() ??
    '';
  const isPrivateEntity = repoVisibility === 'private';
  // Private reveal-only: all tabs are owner-configurable (no protected tabs).
  // Non-private hide-list: Overview and Docs cannot be hidden.

  const uniqueAvailableTabs = useMemo(
    () => Array.from(new Set(availableTabs.filter(Boolean))),
    [availableTabs],
  );

  // Private repos have no protected tabs — all tabs are owner-configurable.
  // Non-private repos use the protectedTabs prop (Overview, Docs cannot be hidden).
  const actualProtectedTabs = useMemo(
    () => {
      if (isPrivateEntity) return [];
      return protectedTabs.filter(tab => uniqueAvailableTabs.includes(tab));
    },
    [isPrivateEntity, protectedTabs, uniqueAvailableTabs],
  );

  const [localSelection, setLocalSelection] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Tracks whether we've already seeded localSelection for the current dialog session
  const [synced, setSynced] = useState(false);

  // Reset synced flag whenever the dialog is closed
  useEffect(() => {
    if (!open) {
      setSynced(false);
    }
  }, [open]);

  // Seed localSelection as soon as data has loaded (handles async API response)
  useEffect(() => {
    if (open && !loading && !synced) {
      const nextSelection = (isPrivateEntity
        ? ownerVisibleTabs
        : ownerHiddenTabs
      ).filter(
        tab =>
          uniqueAvailableTabs.includes(tab) &&
          !actualProtectedTabs.includes(tab),
      );
      setLocalSelection(Array.from(new Set(nextSelection)));
      setSynced(true);
    }
  }, [
    open,
    loading,
    ownerHiddenTabs,
    ownerVisibleTabs,
    synced,
    actualProtectedTabs,
    isPrivateEntity,
    uniqueAvailableTabs,
  ]);

  // Sync local state when dialog animation completes (fast-path for cached
  // data). Only seed if we haven't already — otherwise a late-firing transition
  // (data now loads asynchronously) would overwrite the user's in-progress
  // selection back to the saved value.
  const handleEntered = () => {
    if (!loading && !synced) {
      const nextSelection = (isPrivateEntity
        ? ownerVisibleTabs
        : ownerHiddenTabs
      ).filter(
        tab =>
          uniqueAvailableTabs.includes(tab) &&
          !actualProtectedTabs.includes(tab),
      );
      setLocalSelection(Array.from(new Set(nextSelection)));
      setSynced(true);
    }
  };

  const toggleTab = (title: string) => {
    setLocalSelection(prev =>
      prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title],
    );
  };

  const hideAllTabs = () => {
    const tabs = uniqueAvailableTabs.filter(
      tab => !actualProtectedTabs.includes(tab),
    );
    setLocalSelection(isPrivateEntity ? [] : tabs);
  };

  const showAllTabs = () => {
    const tabs = uniqueAvailableTabs.filter(
      tab => !actualProtectedTabs.includes(tab),
    );
    setLocalSelection(isPrivateEntity ? tabs : []);
  };

  const handleSave = async () => {
    if (isPrivateEntity && securityFindingPrivateLock) {
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      // Strip protected tabs at save-time as an extra safeguard, in case
      // localSelection was seeded with protected tabs or protectedTabs changed.
      const filteredSelection = localSelection.filter(
        tab => !actualProtectedTabs.includes(tab),
      );
      const dedupedSelection = Array.from(new Set(filteredSelection));
      if (isPrivateEntity) {
        await setOwnerVisibleTabs(dedupedSelection);
      } else {
        await setOwnerHiddenTabs(dedupedSelection);
      }
      setSaveError(null);
      // Close immediately on success.
      onClose();
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : 'Failed to save tab settings',
      );
    } finally {
      setSaving(false);
    }
  };

  const selectedCount = localSelection.length;

  return (
    <Dialog open={open} onClose={onClose} TransitionProps={{ onEntered: handleEntered }}>
      <DialogTitle>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <VisibilityOffIcon color="action" fontSize="small" />
          {isPrivateEntity
            ? 'Manage Revealed Tabs for Non-Owners'
            : 'Manage Tab Visibility for Non-Owners'}
        </Box>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {saveError && (
          <Alert severity="error" role="alert" style={{ marginBottom: 12 }}>
            {saveError}
          </Alert>
        )}

        {isPrivateEntity && securityFindingPrivateLock && (
          <Alert severity="warning" role="alert" style={{ marginBottom: 12 }}>
            Public tab reveal is disabled while a security finding lock is active
            on this private repository.
            {securityFindingPrivateLockReason
              ? ` Reason: ${securityFindingPrivateLockReason}`
              : ''}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <Typography variant="body2" className={classes.description}>
              {isPrivateEntity ? (
                <>
                  Select tabs to reveal to non-owners of{' '}
                  <strong>{entity.metadata.name}</strong>. By default,
                  non-owners see the Limited View access tab for
                  private-repo components. Revealed tabs become visible to all
                  users.
                  {actualProtectedTabs.includes('Overview') && (
                    <>
                      {' '}Overview is shown automatically once any tab is
                      revealed.
                    </>
                  )}
                  {actualProtectedTabs.length > 0 && (
                    <>
                      {' '}Protected tabs ({' '}
                      <strong>{actualProtectedTabs.join(', ')}</strong>{' '}
                      ) cannot be configured.
                    </>
                  )}
                </>
              ) : (
                <>
                  Select tabs to hide from non-owners of{' '}
                  <strong>{entity.metadata.name}</strong>. Hidden tabs are
                  visible only to users who own this component.{' '}Protected
                  tabs
                  {actualProtectedTabs.length > 0 && (
                    <>
                      {' '}(<strong>{actualProtectedTabs.join(', ')}</strong>)
                    </>
                  )}{' '}
                  cannot be hidden.
                </>
              )}
            </Typography>
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Available Tabs
            </Typography>
            <Divider style={{ marginBottom: 12 }} />

              <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginBottom: 12 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={hideAllTabs}
                  disabled={
                    saving ||
                    loading ||
                    (isPrivateEntity && securityFindingPrivateLock)
                  }
                >
                  {isPrivateEntity ? 'Reveal none' : 'Hide all'}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={showAllTabs}
                  disabled={
                    saving ||
                    loading ||
                    (isPrivateEntity && securityFindingPrivateLock)
                  }
                >
                  {isPrivateEntity ? 'Reveal all' : 'Show all'}
                </Button>
              </Box>

            <FormGroup className={classes.tabList}>
              {uniqueAvailableTabs.map(title => {
                const isProtected = actualProtectedTabs.includes(title);
                const isSelected = localSelection.includes(title);
                return (
                  <FormControlLabel
                    key={title}
                    control={
                      <Checkbox
                        checked={isSelected}
                        onChange={() => !isProtected && toggleTab(title)}
                        disabled={
                          isProtected ||
                          (isPrivateEntity && securityFindingPrivateLock)
                        }
                        size="small"
                        color="primary"
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center">
                        <span>{title}</span>
                        {isProtected && (
                          <Chip
                            label="protected"
                            size="small"
                            className={classes.hiddenChip}
                            variant="outlined"
                          />
                        )}
                        {isSelected && !isProtected && (
                          <Chip
                            label={isPrivateEntity ? 'revealed' : 'hidden'}
                            size="small"
                            className={classes.hiddenChip}
                            color="secondary"
                          />
                        )}
                      </Box>
                    }
                  />
                );
              })}
            </FormGroup>

            {selectedCount > 0 && (
              <Box className={classes.summaryBox}>
                <Typography variant="body2" color="textSecondary">
                  <strong>{selectedCount}</strong> tab
                  {selectedCount !== 1 ? 's' : ''}{' '}
                  {isPrivateEntity ? 'will be revealed to' : 'will be hidden from'}
                  {' '}non-owners.
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={
            saving ||
            loading ||
            (isPrivateEntity && securityFindingPrivateLock)
          }
        >
          {saving ? <CircularProgress size={16} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
